import { useMemo } from 'react';
import { t } from '@app/i18n';
import { useLocale } from '@app/i18n/useLocale';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  POSTURES_FALLBACK,
  SLUG_PATTERN,
  STATUSES,
  TRUST_DOMAIN_PATTERN,
  type SiteFormValues,
} from './SiteModel';

/**
 * Validação do cadastro de site (P1).
 *
 * O que **não** se valida aqui, e por quê:
 *
 *   - o `spiffe_template`: vazio é valor legítimo («use o padrão»), e a única
 *     verificação útil sobre um template — a colisão de ID com os sites que já
 *     existem — é do servidor, que conhece os outros templates. Reprovar aqui
 *     por forma daria a impressão de que a tela sabe algo que ela não sabe.
 *   - o `executor_endpoint` vazio: é o «middleware não configurado» do FR-119,
 *     estado legítimo de um site que ainda não subiu. O que ele custa é o site
 *     ficar fora do fan-out, e isso a tela mostra na coluna, não no formulário.
 */
export function siteSchema(): yup.ObjectSchema<SiteFormValues> {
  return yup.object({
    slug: yup
      .string()
      .trim()
      .required(t('sites.form.validation.slugRequired'))
      .matches(SLUG_PATTERN, t('sites.form.validation.slugPattern')),
    name: yup.string().trim().required(t('sites.form.validation.nameRequired')),
    // O TRUST DOMAIN É OBRIGATÓRIO NA TELA, e o servidor aceita vazio — a
    // diferença é deliberada.
    //
    // Vazio é estado legítimo no CONTRATO (site migrado antes do S-b, cliente de
    // API), e por isso o backend não recusa. Mas aqui é o formulário onde a pessoa
    // está cadastrando um cluster, e ela é a única que sabe a resposta: um site sem
    // trust domain não prevê SPIFFE ID nenhum — a prévia sai vazia, a colisão não
    // tem o que comparar, e o «Testar conexão» só pode sugerir. Deixar salvar
    // assim entrega um cadastro que não responde à pergunta que o cadastro existe
    // para responder.
    //
    // NÃO é o caso do `executor_endpoint` logo abaixo: aquele é sobre a rede, que
    // pode não existir ainda; este é sobre NOME, que se decide antes do cluster.
    // Opcional desde o aim-p4qh (decisão 3 do mrolim): o Site novo aceita identidades antes do primeiro teste, sem
    // SPIFFE ID previsto, e a leitura do executor grava o valor depois. A regra dos caracteres continua.
    trustDomain: yup
      .string()
      .defined()
      .trim()
      .matches(TRUST_DOMAIN_PATTERN, {
        message: t('sites.form.validation.trustDomainPattern'),
        excludeEmptyString: true,
      }),
    // `url()` do yup aceita `http://` sem host e recusa endereço interno de
    // cluster; o que importa aqui é ser absoluto, e quem julga o resto é a
    // sondagem — que é a verificação de verdade.
    executorEndpoint: yup
      .string()
      .defined()
      .trim()
      .test(
        'absolute',
        t('sites.form.validation.endpointAbsolute'),
        (value) => !value || /^https?:\/\/.+/.test(value),
      ) as yup.Schema<string>,
    posture: yup
      .string()
      .oneOf(POSTURES_FALLBACK)
      .required(t('sites.form.validation.postureRequired')) as yup.Schema<
      SiteFormValues['posture']
    >,
    spiffeTemplate: yup.string().defined().trim() as yup.Schema<string>,
    // As credenciais de borda não são obrigatórias: um site sem elas é o site que
    // ainda não subiu, e o produto responde «middleware não configurado» — estado
    // legítimo, do mesmo tipo que o endpoint vazio acima. Recusar aqui impediria
    // cadastrar o cluster antes de a borda existir.
    cfClientId: yup.string().defined().trim() as yup.Schema<string>,
    // Sem `trim()`: espaço numa senha pode ser da senha.
    cfSecret: yup.string().defined() as yup.Schema<string>,
    status: yup.string().oneOf(STATUSES).required() as yup.Schema<SiteFormValues['status']>,
  });
}

export function useSiteValidation() {
  // O `locale` não aparece no corpo e ainda assim é dependência: as mensagens são
  // montadas na construção do schema, então trocar de idioma com o drawer aberto
  // deixaria a validação no idioma anterior até o próximo `mount`.
  const locale = useLocale();
  // O lint não enxerga a leitura do idioma feita pelo `t()` lá dentro.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => siteSchema(), [locale]);
  return {
    resolver: yupResolver(schema),
    mode: 'onChange' as const,
  };
}
